import { getClient } from "./client";
import { translateError, VixioError } from "./errors";

/**
 * Registro de tipos das RPCs. Cada pacote de UI de módulo acrescenta as suas por declaração:
 *
 *   declare module "@vixio/data" {
 *     interface RpcRegistry {
 *       "sales.advance": { args: { p_opportunity: string; p_to_stage: string; p_note?: string | null }; returns: null };
 *     }
 *   }
 *
 * Com isso `rpc("sales", "advance", {...})` confere argumentos e retorno. RPC fora do registro
 * aceita qualquer objeto e devolve `unknown`.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RpcRegistry {}

type Key<S extends string, F extends string> = `${S}.${F}`;
export type RpcArgs<S extends string, F extends string> = Key<S, F> extends keyof RpcRegistry
  ? RpcRegistry[Key<S, F>] extends { args: infer A }
    ? A
    : Record<string, unknown>
  : Record<string, unknown>;
export type RpcReturn<S extends string, F extends string> = Key<S, F> extends keyof RpcRegistry
  ? RpcRegistry[Key<S, F>] extends { returns: infer R }
    ? R
    : unknown
  : unknown;

/** Chama `schema.fn(args)` por POST (Content-Profile: schema). Erros viram VixioError em pt-BR. */
export async function rpc<S extends string, F extends string>(
  schema: S,
  fn: F,
  args: RpcArgs<S, F> = {} as RpcArgs<S, F>,
): Promise<RpcReturn<S, F>> {
  let res;
  try {
    res = await getClient().schema(schema).rpc(fn, args as Record<string, unknown>);
  } catch (e) {
    throw translateError(e);
  }
  if (res.error) throw translateError(res.error);
  return res.data as RpcReturn<S, F>;
}

/** Leitura de tabela/view: `from("finance", "v_installments").select(...)` com erro traduzido. */
export function from(schema: string, table: string) {
  return getClient().schema(schema).from(table);
}

/** Executa uma consulta do supabase-js e devolve os dados ou lança VixioError. */
export async function unwrap<T>(p: PromiseLike<{ data: T | null; error: unknown }>): Promise<T> {
  let r;
  try {
    r = await p;
  } catch (e) {
    throw translateError(e);
  }
  if (r.error) throw translateError(r.error);
  return r.data as T;
}

export { VixioError };
